import React from 'react'
import Table from 'antd/lib/table'
import { Link, Route } from 'react-router-dom'

import { Spinner } from '~/components'

import * as routes from '~/pages/routes'
import { auth, getProductsRef } from '~/firebase'
import { confirmOrder } from '~/firebase.actions'
import { calculateTotalPrice } from '~/pages/LandingPage/VariableProductState'
import { clearBasketItems, cryptoAddressAPI, removeBasketItem, SYMBOLS } from '~/utils'
import { fetchPrices } from '~/utils/coinApi'
import { BaseProduct, OrderedProduct, OrderObject, PaymentSymbols, Prices, Product, VariableProduct } from '~/types'

import './style.css'
import Orders from '~/pages/Orders';


export default class Basket extends React.Component<{basket: string[]/* in the form of product ids ['0', '2', '3', '-100'] - negative value is the hashrate of a VariableProduct */}> {
  state = {
    didPromiseResolve: 'IDLE' as 'IDLE' | 'LOADING' | 'FINISHED' | 'ERROR',
    priceEUR: 0, // total price
    prices: {} as Prices, // conversion rates for all currencies
    products: {} as Product[],
  }

  dateNow = Date.now()

  orderObject = {
    bankPayment: {
      referenceSymbol: auth.currentUser!.uid,
      variableSymbol: this.dateNow.toString().substr(2, 10),
    },
    basket: [] as OrderedProduct[],
    timestamp: this.dateNow,
  } as OrderObject

  componentDidMount() {
    if (this.props.basket.length) {
      getProductsRef()
        .orderByChild('available')
        .equalTo(true)
        .once('value', snapshot => {
          const products = snapshot!.val() as Product[]
          this.setState({
            products,
            priceEUR: this.props.basket.reduce((p, c) => p + calculateProductPrice(products, c), 0),
          })
        })

      fetchPrices()
        .then(prices => this.setState({prices}))
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (Object.keys(prevState.products).length === 0) return {}
    return {priceEUR: nextProps.basket.reduce((p, c) => p + calculateProductPrice(prevState.products, c), 0)}
  }

  /* STEP ONE */

  removeItem = ev => removeBasketItem(ev.currentTarget.dataset.value)

  /* for static renderBasket(basket: OrderedProduct[]) see Orders.renderBasket method */
  renderBasket = () => {
    const p: Product[] = this.state.products
    const c: boolean = !!this.props.basket.length /* condition */

    return (
    <>
      <Table
        className="basketTable" size="small"
        dataSource={c && Object.keys(p).length ? this.props.basket.map(id => ({id})) : []}
        footer={c ? this.renderBasketFooter : undefined}
        pagination={false}
        locale={{emptyText: c ? <Spinner centered/> : 'No items in basket'}}
        rowKey={() => Math.random().toString()}
      >
        <Table.Column key="description" title="Description" width={610} render={({id}) => {
          const isntVariableProduct = Number(id) > 0
          const key = isntVariableProduct ? id : 0
          const description = isntVariableProduct ? p[key].description : `${-id} MH/s @ €${(calculateProductPrice(p, id) / -id).toFixed(2)}`
          return (
            <>
              <h3>{p[key].name}</h3>
              <p>{description}</p>
            </>
          )
        }}/>
        <Table.Column width={110} key="price" title="Price" render={({id}) => '€' + calculateProductPrice(p, id)}/>
        <Table.Column width={50} render={(text, record, ix) => (
          <a onClick={this.removeItem} data-value={ix}><i className="ion-android-close"/></a>
        )}/>
      </Table>
      <button type="button" disabled={!c} className="checkout-btn ant-btn sc-ifAKCX fcfmNQ ant-btn-primary" onClick={this.confirmBasket}>
        <Link to={routes.BASKET.PAYMENT_METHOD}>Select payment method</Link>
      </button>
    </>
    )
  }

  renderBasketFooter = () => !!Object.keys(this.state.products).length && (
    <table>
      <colgroup>
        <col style={{width: 500}}/>
        <col style={{width: 110}}/>
        <col style={{width: 110}}/>
        <col style={{width:  50}}/>
      </colgroup>
      <tfoot>
        <tr className="ant-table-row">
          <td>
            <input className="ant-input" placeholder="Discount Coupon" type="text"/>
          </td>
          <td>
            <button type="button" className="ant-btn sc-ifAKCX fcfmNQ"><span>Apply</span></button>
          </td>
          <td>{'€' + this.state.priceEUR}</td>
          <td>Total</td>
        </tr>
      </tfoot>
    </table>
  )

  confirmBasket = () => {
    this.orderObject.basket = []
    this.props.basket.forEach(_productId => {
      const isVariableProduct = Number(_productId) <= 0
      const productId = isVariableProduct ? 0 : _productId
      const {available, basePrice, coefficient, defaultComputingPower, maxComputingPower, ...rest} = this.state.products[productId] as VariableProduct & Product // eslint-disable-line no-unused-vars
      if (isVariableProduct) {
        rest.computingPower = -_productId
        rest.price = calculateProductPrice(this.state.products, _productId)
      }
      this.orderObject.basket.push({...rest as BaseProduct, productId})
    })
    this.orderObject.priceEUR = SYMBOLS.format(this.state.priceEUR, 'EUR')
  }

  /* STEP TWO */

  setPaymentMethod = (type: PaymentSymbols) => this.orderObject.paymentMethod = type

  renderPaymentMethods = ({t = this.state.priceEUR, c = this.state.prices}) => (
    <>
      <div className="ant-table ant-table-small cryptoPrices">
        <div className="ant-table-content">
          <div className="ant-table-body">
            <table>
              <colgroup>
                <col style={{width: 140, minWidth: 140}}/>
                <col style={{width: 250, minWidth: 250}}/>
              </colgroup>
              <tbody className="ant-table-tbody">
                <tr className="ant-table-row">
                  <td>EUR</td>
                  <td>{t.toFixed(2)}</td>
                </tr>
                <tr className="ant-table-row">
                  <td>USD</td>
                  <td>{(t * c.USD || 0).toFixed(2)}</td>
                </tr>
                <tr className="ant-table-row">
                  <td>CZK <span className="danger">ŠPATNÝ KURS</span></td>
                  <td>{(t * c.CZK || 0).toFixed(2)}</td>
                </tr>
                <tr className="ant-table-row">
                  <td>BTC</td>
                  <td>{(t * c.BTC || 0).toFixed(5)}</td>
                </tr>
                <tr className="ant-table-row">
                  <td>ETH</td>
                  <td>{(t * c.ETH || 0).toFixed(4)}</td>
                </tr>
                <tr className="ant-table-row">
                  <td>LTC</td>
                  <td>{(t * c.LTC || 0).toFixed(3)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="coinLogoWrapper">
        {/* eslint-disable max-len */}
        <Link to={routes.BASKET.CHECKOUT} onClick={() => this.setPaymentMethod(SYMBOLS.EUR.unit as PaymentSymbols)}><div className="coinLogo eurLogo"/></Link>
        <Link to={routes.BASKET.CHECKOUT} onClick={() => this.setPaymentMethod(SYMBOLS.BTC.unit as PaymentSymbols)}><div className="coinLogo btcLogo"/></Link>
        <Link to={routes.BASKET.CHECKOUT} onClick={() => this.setPaymentMethod(SYMBOLS.ETH.unit as PaymentSymbols)}><div className="coinLogo ethLogo"/></Link>
        <Link to={routes.BASKET.CHECKOUT} onClick={() => this.setPaymentMethod(SYMBOLS.LTC.unit as PaymentSymbols)}><div className="coinLogo ltcLogo"/></Link>
        {/* eslint-enable max-len */}
      </div>

      <p style={{float: 'right', color: 'rgba(0, 0, 0, 0.65)'}}>*Choose one to continue</p>
    </>
  )

  /* STEP THREE */

  renderReview = () => {
    const paymentMethod: PaymentSymbols = this.orderObject.paymentMethod!
    const amount = SYMBOLS.format(this.state.priceEUR * this.state.prices[paymentMethod], paymentMethod)

    /* didPromiseResolve === 'IDLE' */
    let btnClassName = 'ant-btn-primary'
    let btnDisabled = false

    if ( this.state.didPromiseResolve === 'LOADING'
      || this.state.didPromiseResolve === 'FINISHED' ) {
      btnClassName = 'unclickable'
      btnDisabled = true
    } else if (this.state.didPromiseResolve === 'ERROR') {
      btnClassName = 'ant-btn-danger ant-btn-background-ghost ant-btn-dashed'
    }

    return (
      <>
      {
        paymentMethod === 'EUR'
          ? this.renderBankRecipient()
          : this.renderCryptoRecipient(amount)
      }
      <article className="rightHalf">
        <h3>Transfer</h3>
        <div className="ant-table ant-table-small">
          <div className="ant-table-content">
            <div className="ant-table-body">
              <table>
                <colgroup>
                  <col style={{width: 140, minWidth: 140}}/>
                  <col style={{width: 190, minWidth: 190}}/>
                </colgroup>
                {/* <thead className="ant-table-thead">
                  <tr>
                    <th><span>Description</span></th>
                    <th><span>Price</span></th>
                    <th><span/></th>
                  </tr>
                </thead> */}
                <tbody className="ant-table-tbody">
                  <tr className="ant-table-row">
                    <td>Amount</td>
                    <td>{amount}</td>
                  </tr>
                  <tr className="ant-table-row"><td>Currency</td><td>{paymentMethod}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <button className={'checkout-btn ant-btn ' + btnClassName} disabled={btnDisabled}
          type="button" onClick={this.confirmOrder}
        >
          <this.renderSpinner/>
        </button>
      </article>
      </>
    )
  }

  renderBankRecipient = () => (
    <article className="leftHalf">
      <h3>Recipient</h3>

      {Orders.renderBankRecipient(this.orderObject.bankPayment)}

    </article>
  )

  renderCryptoRecipient = (amount: string) => {
    const address = cryptoAddressAPI[this.orderObject.paymentMethod!]
    this.orderObject.cryptoPayment = {address, amount}

    return (
      <article className="leftHalf">
        <h3>Recipient</h3>
        <div className="ant-table ant-table-small domestic">
          <div className="ant-table-content">
            <div className="ant-table-body">
              <table>
                <colgroup>
                  <col style={{width: 140, minWidth: 140}}/>
                  <col style={{width: 250, minWidth: 250}}/>
                </colgroup>
                <tbody className="ant-table-tbody">
                  <tr className="ant-table-row">
                    <td>Address</td>
                    <td><span className="focus">{address}</span><br/><span className="danger">{"This is a one time address for this payment. Don't use it for other payments."}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="ant-table-footer">
              <table>
                <colgroup>
                  <col style={{width: 390 - 32, minWidth: 390 - 32}}/>
                </colgroup>
                <tfoot>
                  <tr>
                    <td>
                      {"We'll send you an email as soon as the transaction is confirmed by the network."}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </article>
    )
  }

  renderSpinner = () => ({
    ERROR: () => <span title="Error: See details in console. You may try again." style={{pointerEvents: 'initial'}}>Error ✗</span>,
    FINISHED: () => <span title="Success! Order is placed." style={{pointerEvents: 'initial'}}>Order is placed ✓</span>,
    IDLE: () => <span>Confirm order</span>,
    LOADING: () => <span>Contacting server <Spinner inline/></span>,
  }[this.state.didPromiseResolve]())

  confirmOrder = () => {
    /* Clean up the orderObject from unrelevant data */

    if (this.orderObject.paymentMethod === 'EUR') delete this.orderObject.cryptoPayment
    else delete this.orderObject.bankPayment

    this.setState({didPromiseResolve: 'LOADING'})

    confirmOrder(this.orderObject)
      .then(() => {
        this.setState({didPromiseResolve: 'FINISHED'})
        setTimeout(clearBasketItems, 4000)
      },
      e => {
        console.error(e)
        this.setState({didPromiseResolve: 'ERROR'})
      })
  }

  /* RENDER */

  render() {
    if (!this.props.basket.length) return (
      <div className="basketWrapper isoLayoutContentWrapper sc-fjdhpX cKswoM">
        <main className="sc-iRbamj gXBuxZ">
          <section>
            <this.renderBasket/>
          </section>
        </main>
      </div>
    )

    return (
      <div className="basketWrapper isoLayoutContentWrapper sc-fjdhpX cKswoM">
        <main className="sc-iRbamj gXBuxZ">

          <Route exact path={routes.BASKET.INDEX} render={() => (
            <section>
              <this.renderBasket/>
            </section>
          )}/>

          <Route exact path={routes.BASKET.PAYMENT_METHOD} render={() => (
            <section className="paymentMethodWrapper">
              <this.renderPaymentMethods/>
            </section>
          )}/>

          <Route exact path={routes.BASKET.CHECKOUT} render={() => (
            <section className="checkoutWrapper">
              <this.renderReview/>
            </section>
          )}/>


        </main>
      </div>
    )
  }
}


function calculateProductPrice(products: Product[], key: string): number {
  if (Number(key) > 0) return products[key].price
  const {basePrice, coefficient} = products[0] as VariableProduct
  return Math.round(calculateTotalPrice(basePrice, coefficient, -key)) // if key is less than 0, it is actually the MH/s with negative sign
}



// WEBPACK FOOTER //
// ./src/pages/Basket/index.tsx